import React from 'react';
import { Building2, Guitar as Hospital, School, Factory, ShoppingBag, Truck, Quote, Star, CheckCircle, Users, Award, Handshake } from 'lucide-react';

const Clientele = () => {
  const partners = [
    {
      name: "Ministry of Health",
      category: "Government",
      logo: "/mintryofhealthlogo.png",
      description: "National healthcare policy implementation and medical waste management standards",
      services: ["Medical Waste Disposal", "Training Programs", "Regulatory Compliance"]
    },
    {
      name: "Mulago Hospital",
      category: "Healthcare",
      logo: "/Mulago-Hospital.jpg",
      description: "Uganda's largest national referral hospital with comprehensive waste management needs",
      services: ["Biohazard Disposal", "Emergency Response", "Medical Equipment Sterilization"]
    },
    {
      name: "Makerere University",
      category: "Education",
      logo: "/makerere_university.jpg",
      description: "Leading educational institution requiring specialized laboratory waste management",
      services: ["Laboratory Waste", "Chemical Disposal", "Research Facility Management"]
    },
    {
      name: "Kampala Pharmaceutical Industries",
      category: "Manufacturing",
      logo: "/kampalapharmacuticallogo.png",
      description: "Pharmaceutical manufacturing with strict waste disposal requirements",
      services: ["Pharmaceutical Waste", "Expired Product Disposal", "Quality Assurance"]
    },
    {
      name: "Joint Medical Stores",
      category: "Healthcare",
      logo: "/JMS_logo-1.png",
      description: "Leading medical procurement and distribution organization in Uganda",
      services: ["Medical Waste Management", "Pharmaceutical Waste", "Regulatory Compliance"]
    },
    {
      name: "World Food Program",
      category: "Humanitarian",
      logo: "/worldfoodprogram.png",
      description: "United Nations agency fighting hunger worldwide with food assistance",
      services: ["Expired Food Disposal", "Waste Management Consulting", "Environmental Compliance"]
    },
    {
      name: "Lubaga Hospital",
      category: "Healthcare",
      logo: "/lubagahospital.png",
      description: "One of Uganda's leading private not-for-profit hospitals",
      services: ["Medical Waste Disposal", "Biohazard Management", "Infection Control"]
    },
    {
      name: "Mengo Hospital",
      category: "Healthcare",
      logo: "/mengohospitallogo.png",
      description: "Oldest hospital in Uganda with comprehensive healthcare services",
      services: ["Waste Management Solutions", "Clinical Waste Disposal", "Healthcare Support Services"]
    },
    {
      name: "East African Medical Vitals",
      category: "Healthcare",
      logo: "/eamv.jpeg",
      description: "Leading provider of medical supplies and equipment in East Africa",
      services: ["Medical Waste Management", "Equipment Disposal", "Regulatory Compliance"]
    }
  ];

  const clientTypes = [
    {
      icon: <Hospital className="w-8 h-8 sm:w-12 sm:h-12" />,
      title: "Healthcare Facilities",
      count: "150+",
      description: "Hospitals, clinics, and medical centers across Uganda",
      services: ["Medical waste disposal", "Pathological waste treatment", "Sharps management"]
    },
    {
      icon: <Factory className="w-8 h-8 sm:w-12 sm:h-12" />,
      title: "Manufacturing",
      count: "80+",
      description: "Industrial facilities and manufacturing plants",
      services: ["Industrial waste management", "Chemical disposal", "Production waste recycling"]
    },
    {
      icon: <School className="w-8 h-8 sm:w-12 sm:h-12" />,
      title: "Educational Institutions",
      count: "45+",
      description: "Universities, colleges, and research facilities",
      services: ["Laboratory waste disposal", "Research material management", "Academic training"]
    },
    {
      icon: <Building2 className="w-8 h-8 sm:w-12 sm:h-12" />,
      title: "Government Agencies",
      count: "25+",
      description: "Municipal and federal government offices",
      services: ["Office waste management", "Document destruction", "Compliance consulting"]
    },
    {
      icon: <ShoppingBag className="w-8 h-8 sm:w-12 sm:h-12" />,
      title: "Retail & Commercial",
      count: "200+",
      description: "Shopping centers, offices, and commercial buildings",
      services: ["General waste management", "Recycling programs", "Consulting services"]
    },
    {
      icon: <Truck className="w-8 h-8 sm:w-12 sm:h-12" />,
      title: "Logistics & Transport",
      count: "30+",
      description: "Transportation companies and logistics providers",
      services: ["Fleet waste management", "Hazmat transportation", "Equipment disposal"]
    }
  ];

  const testimonials = [
    {
      name: "Dr. Sarah Nakamya",
      position: "Chief Medical Officer",
      company: "Mulago Hospital",
      image: "https://images.pexels.com/photos/5327585/pexels-photo-5327585.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop&crop=face",
      quote: "BioHaz Waste Solutions has been instrumental in helping us maintain the highest standards of medical waste management. Their 24/7 emergency response and professional team give us complete confidence in our waste disposal processes."
    },
    {
      name: "James Okello",
      position: "Environmental Health Officer",
      company: "Ministry of Health",
      image: "https://images.pexels.com/photos/5327656/pexels-photo-5327656.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop&crop=face",
      quote: "Their commitment to regulatory compliance and environmental protection aligns perfectly with our national health objectives. BioHaz has become our trusted partner in ensuring safe waste management across Uganda's healthcare system."
    },
    {
      name: "Prof. Margaret Nabwire",
      position: "Research Director",
      company: "Makerere University",
      image: "https://images.pexels.com/photos/5327921/pexels-photo-5327921.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop&crop=face",
      quote: "The specialized handling of our laboratory waste and chemical disposal has been exceptional. Their team understands the unique requirements of academic research and provides tailored solutions that meet our strict safety standards."
    }
  ];

  const achievements = [
    { number: "500+", label: "Active Clients", icon: <Users className="w-5 h-5 sm:w-6 sm:h-6" /> },
    { number: "99.8%", label: "Client Retention Rate", icon: <Handshake className="w-5 h-5 sm:w-6 sm:h-6" /> },
    { number: "4.9/5", label: "Customer Satisfaction", icon: <Star className="w-5 h-5 sm:w-6 sm:h-6" /> },
    { number: "100%", label: "Compliance Record", icon: <Award className="w-5 h-5 sm:w-6 sm:h-6" /> }
  ];

  return (
    <div className="w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 md:py-20 xl:py-24 bg-gradient-to-br from-[#1e4a8a] to-[#295cab] text-white w-full">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12 text-center w-full">
          <h1 className="text-2xl sm:text-4xl md:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6">Our Clientele & Partners</h1>
          <p className="text-base sm:text-lg md:text-xl xl:text-2xl max-w-3xl mx-auto leading-relaxed px-2 sm:px-4">
            Trusted by leading organizations across Uganda for comprehensive waste management solutions
          </p>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-8 sm:py-12 md:py-16 xl:py-20 bg-white w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {achievements.map((achievement, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-blue-50 text-[#295cab] rounded-full mb-3 sm:mb-4">
                  {achievement.icon}
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">{achievement.number}</div>
                <div className="text-gray-600 text-sm sm:text-base">{achievement.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Partners */}
      <section className="py-10 sm:py-14 md:py-16 xl:py-20 bg-gray-50 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-gray-900 mb-4">Key Partners</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Strategic partnerships with leading institutions drive our commitment to excellence
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8 md:gap-10">
            {partners.map((partner, index) => (
              <div key={index} className="bg-white p-6 sm:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-4">
                  <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 bg-white p-2 rounded-lg border border-gray-200 flex items-center justify-center">
                    <img 
                      src={partner.logo} 
                      alt={`${partner.name} logo`} 
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        // Fallback to a placeholder if image fails to load
                        e.currentTarget.src = '/assets/partners/placeholder-logo.png';
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900">{partner.name}</h3>
                      <span className="px-3 py-1 bg-blue-50 text-[#1e4a8a] text-xs sm:text-sm font-medium rounded-full self-start">
                        {partner.category}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4 text-sm sm:text-base">{partner.description}</p>
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900 text-sm sm:text-base">Services Provided:</h4>
                      <div className="flex flex-wrap gap-2">
                        {partner.services.map((service, serviceIndex) => (
                          <span key={serviceIndex} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs sm:text-sm rounded">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Types */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Comprehensive waste management solutions across diverse sectors
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-8 md:gap-10">
            {clientTypes.map((type, index) => (
              <div key={index} className="text-center p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl hover:shadow-lg transition-all duration-300">
                <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-blue-50 text-[#295cab] rounded-full mb-4 sm:mb-6">
                  {type.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{type.title}</h3>
                <div className="text-2xl sm:text-3xl font-bold text-[#295cab] mb-2">{type.count}</div>
                <p className="text-gray-600 mb-4 text-sm sm:text-base">{type.description}</p>
                <div className="space-y-1">
                  {type.services.map((service, serviceIndex) => (
                    <div key={serviceIndex} className="flex items-center justify-center space-x-2">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
                      <span className="text-gray-700 text-xs sm:text-sm">{service}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-[#1e4a8a] to-[#3a7bd5] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">What Our Clients Say</h2>
            <p className="text-lg sm:text-xl max-w-2xl mx-auto px-4">
              Real feedback from the organizations that trust us with their waste management needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm p-6 sm:p-8 rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                <Quote className="w-6 h-6 sm:w-8 sm:h-8 text-blue-300 mb-4" />
                <p className="text-blue-100 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">"{testimonial.quote}"</p>
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-semibold text-sm sm:text-base">{testimonial.name}</h4>
                    <p className="text-blue-200 text-xs sm:text-sm">{testimonial.position}</p>
                    <p className="text-blue-200 text-xs sm:text-sm">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership CTA */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">Join Our Growing Network</h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            Become part of Uganda's most trusted waste management partnership network. 
            Experience the difference of working with industry leaders.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-[#295cab] text-white rounded-lg font-semibold hover:bg-[#1e4a8a] transition-colors text-sm sm:text-base"
            >
              Become a Partner
            </a>
            <a 
              href="tel:+256393254458" 
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 border-2 border-[#295cab] text-[#295cab] rounded-lg font-semibold hover:bg-[#295cab] hover:text-white transition-colors text-sm sm:text-base"
            >
              Schedule Consultation
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Clientele;